// src/views/proprio/ProprioLaneGanttView.jsx — MON CALENDRIER, vue Planning
// (refonte lot ④, spec Dimitri 2026-08-22 §5). LECTURE SEULE.
//
// « ON GARDE LE CALENDRIER, on ne le remplace pas par des cartes. Mais les
// barres doivent tout dire sans survol. »
//
//   · une ligne par logement, le nom FIXE à gauche (120 px, 108 au téléphone),
//     les jours du mois qui défilent horizontalement ;
//
// ⚠️ « FIXE » N'ÉTAIT PAS VRAI AU TÉLÉPHONE (repris de #1269, 30/09). Un
// `position:sticky` posé sur un élément de grille est borné par son BLOC
// CONTENEUR — la boîte de la grille, dont la largeur valait celle du conteneur
// (364 px) alors que le mois débordait à ~1500 px. La colonne des noms
// décrochait donc au bout de quelques jours de défilement, et comme l'écran se
// recentrait seul sur aujourd'hui, le Planning s'ouvrait sans AUCUN nom de
// logement dès le 9 du mois. Correctif : la grille prend la largeur de son
// contenu (`width: max-content`, jamais moins que le conteneur). Et au
// téléphone, plus de recentrage automatique (arbitrage Dimitri, #1269) :
// « mieux vaut voir le début du mois que d'être perdu ». Conservé sur ordinateur.
//
// LE NOM affiché passe par `nomLogementProprio` : le serveur remplit `nom` avec
// le CODE quand le logement n'a pas de nom — on écrit « Votre logement ».
//   · CHAQUE BARRE PORTE DEUX LIGNES : le nom du voyageur (10.5 gras) puis
//     « 6 nuits · 2 pers. · 780 € » (8.5, .75) — une donnée absente s'écrit
//     « — », JAMAIS un zéro inventé (nb personnes inconnu en KV, montant tant
//     que la facturation n'est pas établie) ;
//   · LA COULEUR DIT LA SOURCE — Airbnb #f5c9b8 · Booking #c3d9ee · Direct
//     #bfe0ce · Bloqué #e6eaee, chacune avec son bord ; texte sombre ;
//   · trait « aujourd'hui » #c1512d à demi-opacité ;
//   · LÉGENDE EN PIED sur #f8fafc, les quatre couleurs nommées ;
//   · PLUS RIEN AU SURVOL — tout est écrit sur la barre ;
//   · AU CLIC : la feuille du séjour — source en bandeau coloré, logement,
//     montant 20 px, trois faits à icônes (dates · nuits · personnes) et
//     « Les messages de ce séjour · N échanges » (lecture seule, avec la
//     phrase « C'est FullBnbZen qui répond aux voyageurs. »).
//
// LE MOIS est piloté par les flèches du bandeau (prop `mois` — plus de nav
// interne ni de mémoire localStorage). LECTURE SEULE STRICTE inchangée :
// uniquement fetchProprio* — aucun write path, aucun marqueur d'ops interne.

import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import {
  CalendarRange, ChevronLeft, ChevronRight, X, Moon, Users, MessageSquare,
} from 'lucide-react'
import { DS } from '../../lib/ui'
import { fmtE } from '../../lib/format'
import { nomLogementProprio } from '../../lib/proprio-nom-logement'
import {
  fetchProprioCalendar, fetchProprioPricingCalendar, fetchProprioLogements,
  fetchProprioMessages,
} from '../../lib/proprio-api'
import { SectionTitle, Loading, ErrorBox, ReadOnlyHint } from './_proprioUi'
import { monthDaysIso, computeMonthBars, buildCoveredSet, buildPricingIndex } from './_ganttGeometry'
import useEtroit from '../../lib/useEtroit'
import useEchapPourFermer from '../../lib/useEchapPourFermer'

const now = new Date()
const MONTHS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre']
const MONTHS_MIN = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre']
const DAY_LETTERS = ['D', 'L', 'M', 'M', 'J', 'V', 'S']

// LA COULEUR DIT LA SOURCE (spec §5) — fond + bord + libellé. Texte sombre
// (#233240) : les pastels portent une écriture, pas un aplat.
export const SOURCES = {
  airbnb: { bg: '#f5c9b8', bord: '#e0b5a5', label: 'Airbnb' },
  booking: { bg: '#c3d9ee', bord: '#a8c4dd', label: 'Booking' },
  direct: { bg: '#bfe0ce', bord: '#9ccbb2', label: 'Direct' },
  block: { bg: '#e6eaee', bord: '#d0d7de', label: 'Bloqué' },
}
const CANCEL_BG = '#e8edf2'
const TRAIT_AUJOURDHUI = 'rgba(193, 81, 45, .5)' // #c1512d à demi-opacité

const HEADER_H = 46
const ROW_H = 54
const COL_MIN = 46

const isoOfLocal = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

const isCancelled = (it) => /cancel|annul/i.test(it.status || '')
const isDirect = (it) => it._block && (it.guest_first_name || it.guest_last_name)
const isPureBlock = (it) => it._block && !it.guest_first_name && !it.guest_last_name

export function sourceOf(it) {
  if (isPureBlock(it)) return SOURCES.block
  if (isDirect(it)) return SOURCES.direct
  return SOURCES[(it.source || '').toLowerCase()] || SOURCES.direct
}
function label(it) {
  if (isPureBlock(it)) return 'Blocage'
  return [it.guest_first_name, it.guest_last_name].filter(Boolean).join(' ') || 'Voyageur'
}

// Nuits d'un séjour (différence de dates — jamais un champ inventé).
export function nuitsDe(it) {
  const ci = String(it?.start_date || '').slice(0, 10)
  const co = String(it?.end_date || '').slice(0, 10)
  if (!ci || !co) return null
  const n = Math.round((new Date(`${co}T00:00:00Z`) - new Date(`${ci}T00:00:00Z`)) / 86400000)
  return n > 0 ? n : null
}

// LA SECONDE LIGNE de la barre : « 6 nuits · 2 pers. · 780 € » — une donnée
// absente s'écrit « — » (règle Dimitri : jamais un zéro inventé).
export function ligne2(it) {
  if (isPureBlock(it)) return null
  const n = nuitsDe(it)
  const a = Number(it.total_amount)
  return [
    n != null ? `${n} nuit${n > 1 ? 's' : ''}` : '—',
    it.guest_count != null ? `${it.guest_count} pers.` : '—',
    Number.isFinite(a) && a > 0 ? fmtE(a) : '—',
  ].join(' · ')
}

// « 19 → 24 août » (règle des dates : jamais d'année ni de jour de semaine).
export function plageEnClair(it) {
  const f = (iso) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''))
    return m ? `${Number(m[3])} ${MONTHS_MIN[Number(m[2]) - 1]}` : '—'
  }
  return `${f(it.start_date)} → ${f(it.end_date)}`
}

function monthBounds(y, m1) {
  const last = new Date(y, m1, 0).getDate()
  const mm = String(m1).padStart(2, '0')
  return { from: `${y}-${mm}-01`, to: `${y}-${mm}-${String(last).padStart(2, '0')}` }
}

function isWeekendIso(iso) {
  const wd = new Date(`${iso}T12:00:00`).getDay()
  return wd === 0 || wd === 6
}

export default function ProprioLaneGanttView({ mois = null }) {
  const etroit = useEtroit()
  const [yLocal, setY] = useState(now.getFullYear())
  const [m1Local, setM1] = useState(now.getMonth() + 1)
  const y = mois ? mois.y : yLocal
  const m1 = mois ? mois.m1 : m1Local
  const [logements, setLogements] = useState([])
  const [state, setState] = useState({ loading: true, error: null, reservations: [], blocks: [], pricing: [] })
  const [selected, setSelected] = useState(null)
  const todayHeaderRef = useRef(null)
  const didScrollRef = useRef(false)

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }))
    setSelected(null)
    try {
      const { from, to } = monthBounds(y, m1)
      const [cal, price, logs] = await Promise.all([
        fetchProprioCalendar({ from, to }),
        fetchProprioPricingCalendar({ from, to }),
        fetchProprioLogements(),
      ])
      setLogements(logs.logements || [])
      setState({
        loading: false,
        error: null,
        reservations: cal.reservations || [],
        blocks: cal.blocks || [],
        pricing: price.pricing || [],
      })
    } catch (e) {
      setState({ loading: false, error: e.message, reservations: [], blocks: [], pricing: [] })
    }
  }, [y, m1])

  useEffect(() => { load() }, [load])

  const navM = (dir) => {
    const d = new Date(y, m1 - 1 + dir, 1)
    setY(d.getFullYear())
    setM1(d.getMonth() + 1)
  }

  const daysIso = useMemo(() => monthDaysIso(y, m1), [y, m1])
  const N = daysIso.length
  const todayIso = isoOfLocal(now)
  // 90 px au téléphone ne suffisaient pas : deux biens du même immeuble
  // (« Studio Capitole — 2ᵉ étage » / « … — 4ᵉ étage ») rendaient LE MÊME texte
  // tronqué sur deux lignes. 108 px + trois lignes lèvent l'ambiguïté.
  const LEFT_W = etroit ? 108 : 120

  const logIdToRow = useMemo(() => {
    const m = new Map()
    logements.forEach((l, i) => m.set(l.id, i))
    return m
  }, [logements])

  const items = useMemo(
    () => [
      ...state.reservations.map((r) => ({ ...r, _block: false })),
      ...state.blocks.map((b) => ({ ...b, _block: true })),
    ],
    [state.reservations, state.blocks],
  )

  const bars = useMemo(() => computeMonthBars(items, daysIso, logIdToRow), [items, daysIso, logIdToRow])
  const coveredSet = useMemo(
    () => buildCoveredSet(items.filter((it) => !isCancelled(it)), daysIso),
    [items, daysIso],
  )
  const pricingIndex = useMemo(() => buildPricingIndex(state.pricing), [state.pricing])

  // Pas de recentrage automatique au téléphone (arbitrage Dimitri, #1269).
  useEffect(() => {
    if (etroit) return
    if (didScrollRef.current) return
    if (!todayHeaderRef.current) return
    didScrollRef.current = true
    todayHeaderRef.current.scrollIntoView({ behavior: 'auto', inline: 'center', block: 'nearest' })
  }, [bars, etroit])

  const empty = !state.loading && !state.error && logements.length > 0 && items.length === 0
  const noLogements = !state.loading && !state.error && logements.length === 0
  const nomDe = (lid) => nomLogementProprio(logements.find((l) => l.id === lid))

  const gridTemplateColumns = `${LEFT_W}px repeat(${N}, minmax(${COL_MIN}px, 1fr))`
  const gridTemplateRows = `${HEADER_H}px repeat(${logements.length}, ${ROW_H}px)`

  return (
    <div>
      <SectionTitle icon={CalendarRange} sub="Planning de vos logements, un logement par ligne (consultation).">Planning d'occupation</SectionTitle>
      <ReadOnlyHint />

      {/* Nav interne UNIQUEMENT en autonome (le bandeau porte les flèches). */}
      {!mois && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <button onClick={() => navM(-1)} aria-label="Mois précédent" className="fbz-tap fbz-press" style={navBtn}><ChevronLeft size={18} strokeWidth={2} /></button>
          <div style={{ fontFamily: DS.fontDisplay, fontWeight: 600, color: DS.marineDark, fontSize: 16, minWidth: 150, textAlign: 'center' }}>{MONTHS[m1 - 1]} {y}</div>
          <button onClick={() => navM(1)} aria-label="Mois suivant" className="fbz-tap fbz-press" style={navBtn}><ChevronRight size={18} strokeWidth={2} /></button>
        </div>
      )}

      {state.loading && <Loading />}
      {!state.loading && state.error && <ErrorBox message={state.error} onRetry={load} />}

      {!state.loading && !state.error && !noLogements && (
        <div style={{ overflow: 'auto', maxHeight: 'calc(100vh - 260px)', border: `1px solid ${DS.border}`, borderRadius: 10, position: 'relative', background: DS.white, WebkitOverflowScrolling: 'touch' }}>
          {/* ⚠️ `width: max-content` + `minWidth: '100%'` = LE correctif du sticky :
              la grille fait la largeur du mois entier, le bloc conteneur du
              sticky couvre tout le défilement, la colonne des noms tient. */}
          <div data-testid="lane-grille"
            style={{ display: 'grid', gridTemplateColumns, gridTemplateRows, position: 'relative',
              width: 'max-content', minWidth: '100%' }}>

            <div style={{ gridColumn: 1, gridRow: 1, position: 'sticky', top: 0, left: 0, zIndex: 4, background: DS.white, borderRight: `1px solid ${DS.border}`, borderBottom: `1px solid ${DS.border}`, padding: '10px 10px', fontSize: 11, fontWeight: 700, color: DS.marine, display: 'flex', alignItems: 'center' }}>
              Logement
            </div>

            {daysIso.map((iso, i) => {
              const isToday = iso === todayIso
              const we = isWeekendIso(iso)
              const dayNum = Number(iso.slice(8, 10))
              const wd = new Date(`${iso}T12:00:00`).getDay()
              return (
                <div key={'h' + iso} ref={isToday ? todayHeaderRef : null}
                  style={{ gridColumn: i + 2, gridRow: 1, position: 'sticky', top: 0, zIndex: 2, background: isToday ? '#fdf4f0' : (we ? '#f4f4f5' : DS.white), borderBottom: isToday ? `2px solid ${TRAIT_AUJOURDHUI}` : `1px solid ${DS.border}`, borderRight: '1px solid #f0f4f8', padding: '4px 2px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ fontFamily: DS.fontDisplay, fontSize: 14, fontWeight: 600, color: isToday ? '#8f2f1d' : DS.marine }}>{dayNum}</div>
                  <div style={{ fontSize: 9, color: DS.textMuted }}>{DAY_LETTERS[wd]}</div>
                </div>
              )
            })}

            {logements.map((l, li) => (
              <LaneRow
                key={l.id}
                logement={l}
                rowIdx={li}
                daysIso={daysIso}
                todayIso={todayIso}
                pricingForLog={pricingIndex.get(l.id) || null}
                coveredForLog={coveredSet.get(l.id) || null}
                etroit={etroit}
              />
            ))}

            {/* Le trait « aujourd'hui » — #c1512d à demi-opacité (spec §5). */}
            {daysIso.includes(todayIso) && (
              <div aria-hidden="true" style={{ gridColumn: daysIso.indexOf(todayIso) + 2, gridRow: '1 / -1', borderLeft: `2px solid ${TRAIT_AUJOURDHUI}`, pointerEvents: 'none', zIndex: 0 }} />
            )}

            {/* LES BARRES — deux lignes, couleur de source, RIEN au survol. */}
            {bars.map(({ item, rowIdx, startCol, endCol }) => {
              const cancelled = isCancelled(item)
              const src = sourceOf(item)
              const l2 = ligne2(item)
              return (
                <button
                  key={`${item._block ? 'b' : 'r'}-${item.id}-${startCol}`}
                  onClick={() => setSelected(item)}
                  data-testid={`barre-${item._block ? 'b' : 'r'}-${item.id}`}
                  className="fbz-press"
                  style={{
                    gridColumn: `${startCol + 2} / ${endCol + 3}`,
                    gridRow: rowIdx + 2,
                    margin: '6px 2px', height: ROW_H - 12,
                    borderRadius: 7,
                    background: cancelled ? CANCEL_BG : src.bg,
                    border: `1px solid ${cancelled ? '#cbd5e1' : src.bord}`,
                    opacity: cancelled ? 0.85 : 1,
                    padding: '3px 8px',
                    cursor: 'pointer', position: 'relative',
                    display: 'flex', flexDirection: 'column', justifyContent: 'center',
                    alignItems: 'flex-start', gap: 1,
                    overflow: 'hidden', whiteSpace: 'nowrap',
                    zIndex: 1,
                    fontFamily: DS.fontBody, textAlign: 'left',
                  }}>
                  <span style={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis',
                    fontSize: 10.5, fontWeight: 700, color: '#233240',
                    textDecoration: cancelled ? 'line-through' : 'none' }}>
                    {label(item)}{cancelled ? ' · annulée' : ''}
                  </span>
                  {l2 && !cancelled && (
                    <span style={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis',
                      fontSize: 8.5, fontWeight: 600, color: '#233240', opacity: 0.75 }}>
                      {l2}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* LA LÉGENDE EN PIED — fond #f8fafc, les quatre couleurs nommées. */}
      {!state.loading && !state.error && !noLogements && (
        <div data-testid="legende-sources"
          style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center',
            background: '#f8fafc', border: '1px solid #eef2f6', borderRadius: 9,
            padding: '9px 13px', marginTop: 10, fontSize: 11, color: '#3d5166' }}>
          {Object.values(SOURCES).map((s) => (
            <span key={s.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 12, height: 12, borderRadius: 3, background: s.bg, border: `1px solid ${s.bord}`, display: 'inline-block' }} />
              {s.label}
            </span>
          ))}
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 12, height: 12, borderRadius: 3, background: CANCEL_BG, border: '1px solid #cbd5e1', display: 'inline-block' }} />
            <span style={{ textDecoration: 'line-through' }}>Annulée</span>
          </span>
          <span style={{ color: DS.textMuted }}>Le prix du jour s'affiche sous les nuits libres.</span>
        </div>
      )}

      {noLogements && (
        <div style={{ marginTop: 4, padding: 16, background: DS.cielSoft, border: `1px dashed ${DS.borderHover}`, borderRadius: DS.radiusCard, fontSize: 13, color: DS.textSecondary, textAlign: 'center' }}>
          Aucun logement à afficher pour l'instant.
        </div>
      )}
      {empty && (
        <div style={{ marginTop: 14, padding: 16, background: DS.cielSoft, border: `1px dashed ${DS.borderHover}`, borderRadius: DS.radiusCard, fontSize: 13, color: DS.textSecondary, textAlign: 'center' }}>
          Aucune occupation à afficher pour ce mois.
        </div>
      )}

      {selected && (
        <FeuilleSejour item={selected} nomLogement={nomDe(selected.logement_id)}
          etroit={etroit} onClose={() => setSelected(null)} />
      )}
    </div>
  )
}

function LaneRow({ logement, rowIdx, daysIso, todayIso, pricingForLog, coveredForLog, etroit }) {
  const gridRow = rowIdx + 2
  const nom = nomLogementProprio(logement)
  return (
    <>
      <div data-testid={`lane-nom-${logement.id}`} title={nom}
        style={{ gridColumn: 1, gridRow, position: 'sticky', left: 0, zIndex: 3, background: DS.white, borderRight: `1px solid ${DS.border}`, borderBottom: '1px solid #f0f4f8', padding: etroit ? '6px 7px' : '8px 10px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        {/* Trois lignes au téléphone : deux ne suffisaient pas (voir LEFT_W). */}
        <div style={{ fontFamily: DS.fontDisplay, fontSize: etroit ? 11 : 12.5, fontWeight: 600, color: DS.marineDark, lineHeight: 1.18, display: '-webkit-box', WebkitLineClamp: etroit ? 3 : 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', overflowWrap: 'anywhere' }}>
          {nom}
        </div>
      </div>
      {daysIso.map((iso, i) => {
        const we = isWeekendIso(iso)
        const isToday = iso === todayIso
        const covered = coveredForLog ? coveredForLog.has(iso) : false
        const cell = !covered && pricingForLog ? pricingForLog.get(iso) : null
        const price = cell && cell.price1 != null ? cell.price1 : null
        const minStay = cell && Number(cell.min_stay) > 1 ? Number(cell.min_stay) : null
        return (
          <div key={'c' + rowIdx + '-' + i}
            style={{ gridColumn: i + 2, gridRow, background: isToday ? 'rgba(193, 81, 45, 0.05)' : (we ? '#f6f6f7' : 'transparent'), borderRight: '1px solid #f0f4f8', borderBottom: '1px solid #f0f4f8', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
            {price != null && (
              <span style={{ fontSize: 10.5, fontWeight: 600, color: DS.textSecondary, fontFamily: DS.fontBody, lineHeight: 1.05, fontVariantNumeric: 'tabular-nums' }}>{price}</span>
            )}
            {minStay != null && (
              <span style={{ fontSize: 8.5, color: DS.textMuted, fontFamily: DS.fontBody, lineHeight: 1 }}>min {minStay}</span>
            )}
          </div>
        )
      })}
    </>
  )
}

// ── LA FEUILLE DU SÉJOUR (spec §5) — LECTURE SEULE. Téléphone : feuille du bas
// (r18, voile .35, poignée) ; ordinateur : carte centrée. Source en bandeau
// coloré, logement, montant 20 px, trois faits à icônes, messages du séjour. ──
function FeuilleSejour({ item, nomLogement, etroit, onClose }) {
  useEchapPourFermer(onClose)
  const [messagesOuverts, setMessagesOuverts] = useState(false)
  const [msgs, setMsgs] = useState(null) // null = pas chargés
  const cancelled = isCancelled(item)
  const src = sourceOf(item)
  const bloc = isPureBlock(item)
  const n = nuitsDe(item)
  const a = Number(item.total_amount)
  const montant = Number.isFinite(a) && a > 0 ? fmtE(a) : '—'
  // Les messages n'existent que pour une résa V2 (l'inbox référence
  // reservation_id) — une résa KV ou un blocage n'a pas de fil à montrer.
  const peutAvoirMessages = !bloc && !item.kv && item.id != null

  useEffect(() => {
    if (!peutAvoirMessages) return
    let alive = true
    fetchProprioMessages({ logementId: item.logement_id })
      .then((r) => {
        if (!alive) return
        const duSejour = (r?.messages || []).filter((m) => m.reservation_id === item.id)
        setMsgs(duSejour)
      })
      .catch(() => { if (alive) setMsgs([]) })
    return () => { alive = false }
  }, [peutAvoirMessages, item.id, item.logement_id])

  const conteneur = etroit
    ? { position: 'fixed', left: 0, right: 0, bottom: 0, maxHeight: '78vh', overflowY: 'auto',
        background: '#fff', borderRadius: '18px 18px 0 0', padding: '8px 16px 18px', zIndex: 101 }
    : { position: 'fixed', left: '50%', top: '50%', transform: 'translate(-50%, -50%)',
        width: 'min(440px, 92vw)', maxHeight: '82vh', overflowY: 'auto',
        background: '#fff', borderRadius: 14, padding: '14px 18px 18px', zIndex: 101,
        boxShadow: '0 12px 40px rgba(35,50,64,.25)' }

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(35,50,64,.35)', zIndex: 100 }} />
      <div role="dialog" aria-modal="true" data-testid="feuille-sejour" style={conteneur}>
        {etroit && (
          <div aria-hidden="true" style={{ width: 36, height: 4, borderRadius: 2, background: '#d5e3ee', margin: '4px auto 10px' }} />
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <span style={{ flex: 1, fontSize: 14.5, fontWeight: 700, color: '#233240', fontFamily: DS.fontBody }}>
            {bloc ? 'Blocage' : label(item)}{cancelled ? ' · annulée' : ''}
          </span>
          <button type="button" aria-label="Fermer" onClick={onClose} className="fbz-press"
            style={{ width: 32, height: 32, borderRadius: 9, border: 'none', background: '#f4f6f8',
              color: '#3d5166', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            <X size={16} strokeWidth={2.2} />
          </button>
        </div>

        {/* LA SOURCE EN BANDEAU COLORÉ. */}
        <div style={{ background: src.bg, border: `1px solid ${src.bord}`, borderRadius: 9,
          padding: '7px 11px', fontSize: 11.5, fontWeight: 700, color: '#233240', marginBottom: 10 }}>
          {src.label}{bloc ? '' : ` · ${nomLogement}`}
        </div>
        {bloc && (
          <div style={{ fontSize: 11.5, color: '#5c6773', marginBottom: 10 }}>{nomLogement}</div>
        )}

        {/* LE MONTANT en 20 px — « — » si la donnée n'existe pas (KV pas
            encore facturé, blocage). Jamais un zéro inventé. */}
        {!bloc && (
          <div style={{ fontFamily: DS.fontDisplay, fontSize: 20, fontWeight: 700, color: '#233240', marginBottom: 10 }}>
            {montant}
          </div>
        )}

        {/* LES TROIS FAITS, avec leurs icônes. */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginBottom: 12 }}>
          {[
            [CalendarRange, plageEnClair(item)],
            [Moon, n != null ? `${n} nuit${n > 1 ? 's' : ''}` : '—'],
            ...(bloc ? [] : [[Users, item.guest_count != null ? `${item.guest_count} personne${Number(item.guest_count) > 1 ? 's' : ''}` : '—']]),
          ].map(([Icone, texte], i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 12.5, color: '#3d5166' }}>
              <span style={{ width: 26, height: 26, borderRadius: 7, background: '#eef2f6',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icone size={14} strokeWidth={2} color="#3d5166" />
              </span>
              {texte}
            </div>
          ))}
        </div>

        {/* LES MESSAGES DE CE SÉJOUR — lecture seule. */}
        {peutAvoirMessages && msgs && msgs.length > 0 && (
          <button type="button" data-testid="bouton-messages-sejour"
            onClick={() => setMessagesOuverts((v) => !v)}
            className="fbz-press"
            style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%',
              padding: '11px 12px', background: '#f8fafc', border: '1px solid #e5eaf0',
              borderRadius: 10, fontSize: 12.5, fontWeight: 700, color: '#233240',
              cursor: 'pointer', fontFamily: DS.fontBody }}>
            <MessageSquare size={15} strokeWidth={2} color="#3d5166" />
            Les messages de ce séjour · {msgs.length} échange{msgs.length > 1 ? 's' : ''}
          </button>
        )}
        {messagesOuverts && msgs && (
          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 10.5, color: '#5c6773', fontStyle: 'italic', marginBottom: 8 }}>
              Vous lisez les échanges. C'est FullBnbZen qui répond aux voyageurs.
            </div>
            {msgs.map((m) => (
              <div key={m.id} style={{ padding: '8px 10px', marginBottom: 6, borderRadius: 9,
                background: m.direction === 'out' ? '#e8f2fa' : '#f8fafc',
                border: '1px solid #e5eaf0' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#3d5166', marginBottom: 2 }}>
                  {m.direction === 'out' ? 'FullBnbZen' : (m.sender_name || 'Voyageur')}
                </div>
                <div style={{ fontSize: 11.5, color: '#233240', whiteSpace: 'pre-wrap' }}>{m.body}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  )
}

const navBtn = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: DS.card, border: `0.5px solid ${DS.borderHover}`, borderRadius: DS.radiusControl, width: 34, height: 34, color: DS.marine, cursor: 'pointer' }
